import type {UUID} from 'src/typings';
import type {DataSource, HierarchicalRecordRow, RecordRow, SourceAwareRecordRow} from './types';
import {exists} from 'shared/utils/custom-utils';
import {asUuid} from 'shared/utils/types-utils';

export function parseRecordIdFromRowId(rowId: string): UUID {
    return asUuid(rowId.split('.').at(-1));
}

export function isDraft(recordRow: RecordRow): boolean {
    return recordRow.draft;
}

export function isSubrow(recordRow: SourceAwareRecordRow): boolean {
    return recordRow.dataSource === 'subrow';
}

export function removeHierarchyProperties(hierarchicalRecordRow: HierarchicalRecordRow): SourceAwareRecordRow {
    return {
        fields: hierarchicalRecordRow.fields,
        fond: hierarchicalRecordRow.fond,
        id: hierarchicalRecordRow.id,
        draft: hierarchicalRecordRow.draft,
        status: hierarchicalRecordRow.status,
        editLockStatus: hierarchicalRecordRow.editLockStatus,
        editPermission: hierarchicalRecordRow.editPermission,
        deletePermission: hierarchicalRecordRow.deletePermission,
        name: hierarchicalRecordRow.name,
        recordStatusTransitions: hierarchicalRecordRow.recordStatusTransitions,
        dataSource: hierarchicalRecordRow.dataSource,
        parentRecordRowId: hierarchicalRecordRow.parentRowId,
        recordHoldings: hierarchicalRecordRow.recordHoldings,
        assignableDepartments: hierarchicalRecordRow.assignableDepartments,
    };
}

export function markRowWithDataSource(recordRow: RecordRow, dataSource: DataSource, parentRecordRowId: UUID | null): SourceAwareRecordRow {
    return {...recordRow, dataSource, parentRecordRowId}
}

export function markRowsWithDataSource(rows: RecordRow[], dataSource: DataSource, parentRecordRowId: UUID | null): SourceAwareRecordRow[] {
    return rows.map((row) => markRowWithDataSource(row, dataSource, parentRecordRowId))
}

export function createRootOnlyHierarchicalRows(rows: SourceAwareRecordRow[]): HierarchicalRecordRow[] {
    if (rows.length === 0) {
        return [];
    }

    return rows.map((row) => ({...row, subrows: [], parentRowId: null}))
}

export function createHierarchicalRows(rows: SourceAwareRecordRow[], referenceRecordId: UUID): HierarchicalRecordRow[] {
    if (rows.length === 0) {
        return [];
    }

    const hierarchicalRecordRowFactory: HierarchicalRecordRowFactory = (row, subrows, parentRowId) => ({
        ...row,
        subrows: subrows.toSorted(compareRecordRowsWithDraftsLast),
        parentRowId,
    })

    return transformIntoHierarchyRows(rows, referenceRecordId, hierarchicalRecordRowFactory);
}


function compareRecordRowsWithDraftsLast(a: RecordRow, b: RecordRow): number {
    if (a.draft === b.draft) {
        return 0;
    }
    return a.draft ? 1 : -1;
}

function transformIntoHierarchyRows(rows: SourceAwareRecordRow[], referenceRecordId: UUID, hierarchicalRecordRowFactory: HierarchicalRecordRowFactory): HierarchicalRecordRow[] {
    const loadedRecordRowsIds = new Set(rows.map((row) => row.id));

    const isDescendantRow: FieldPredicate = (row) => {
        return row.parentRecordRowId !== referenceRecordId && loadedRecordRowsIds.has(row.parentRecordRowId);
    };

    const {matched: descendantRows, nonMatched: rootRows} = partitionRowsByPredicate(rows, isDescendantRow);

    return rootRows.flatMap((rootRow) => transformIntoHierarchyRow(rootRow, null, descendantRows, hierarchicalRecordRowFactory))
}

function transformIntoHierarchyRow(currentRow: SourceAwareRecordRow, parentRow: SourceAwareRecordRow | null, untransformedRows: SourceAwareRecordRow[], hierarchicalRecordRowFactory: HierarchicalRecordRowFactory): HierarchicalRecordRow {
    const isDirectChildRow: FieldPredicate = (row) => {
        return row.parentRecordRowId === currentRow.id;
    }
    const {matched: directChildRows, nonMatched: otherDescendantRows} = partitionRowsByPredicate(untransformedRows, isDirectChildRow);

    const subtrees = directChildRows.flatMap((directChildRow) => transformIntoHierarchyRow(directChildRow, currentRow, otherDescendantRows, hierarchicalRecordRowFactory));

    return hierarchicalRecordRowFactory(currentRow, subtrees, exists(parentRow) ? parentRow.id : null);
}

function partitionRowsByPredicate(rows: SourceAwareRecordRow[], predicate: FieldPredicate): { matched: SourceAwareRecordRow[]; nonMatched: SourceAwareRecordRow[] } {
    const matched: SourceAwareRecordRow[] = [];
    const nonMatched: SourceAwareRecordRow[] = [];

    // optimization: for-loop is faster than functional array functions
    // eslint-disable-next-line @typescript-eslint/prefer-for-of
    for (let i = 0; i < rows.length; i++) {
        const row = rows[i];

        if (predicate(row)) {
            matched.push(row);
        } else {
            nonMatched.push(row);
        }
    }

    return {matched, nonMatched};
}

type HierarchicalRecordRowFactory = (row: SourceAwareRecordRow, subtrees: HierarchicalRecordRow[], parentRowId: UUID) => HierarchicalRecordRow;
type FieldPredicate = (row: SourceAwareRecordRow) => boolean;